import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ContactRequest, ContactResponse, NoteRequest, NotesResponse } from './types';
import { useParams } from 'react-router-dom';
import { createNote, deleteNote, getContact, getNotes, updateContact, updateNote } from './api';
import { queryClient } from 'providers/AllProviders';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetContact(id: number, options?: UseQueryOptions<ContactResponse, Error>) {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useQuery<ContactResponse>({
    queryKey: ['contact', workspaceId, id],
    queryFn: () => getContact(workspaceId!, id!),
    enabled: Boolean(workspaceId && id),
    ...options
  });
}

export function useUpdateContact(id: number) {
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();

  return useMutation({
    mutationFn: (payload: ContactRequest) => updateContact(workspaceId!, id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['contact', workspaceId, id],
        refetchType: 'active'
      });
    }
  });
}

export function useGetNotes(
  id: number,
  options?: UseQueryOptions<{ data: NotesResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useQuery<{ data: NotesResponse[]; meta: MetaApiResponse }>({
    queryKey: ['notes', workspaceId, id],
    queryFn: () => getNotes(workspaceId!, id!),
    enabled: Boolean(workspaceId && id),
    ...options
  });
}

export function useCreateNote(id: number) {
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();

  return useMutation({
    mutationFn: (payload: NoteRequest) => createNote(workspaceId!, id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', workspaceId, id],
        refetchType: 'active'
      });
    }
  });
}

export function useUpdateNote(contactId: number) {
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: NoteRequest }) =>
      updateNote(workspaceId!, contactId!, id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', workspaceId, contactId],
        refetchType: 'active'
      });
    }
  });
}

export function useDeleteNote(contactId: number) {
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();

  return useMutation({
    mutationFn: (noteId: number) => deleteNote(workspaceId!, contactId!, noteId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', workspaceId, contactId],
        refetchType: 'active'
      });
    }
  });
}
